/**
 * Social Planner (SPEC PX-3 - social-planner R2).
 *
 * A content-calendar + compose + schedule surface for the multi-network
 * `social_posts` collection. Lists the tenant's posts sorted by scheduled_at with
 * per-post network badges + per-network status, a compose dialog (title, caption,
 * media URLs, network checkboxes, scheduled_at, draft vs schedule), and
 * edit/delete actions wired to the R2 CRUD router. Writes gated on
 * useCanWrite().
 *
 * STALE (SPEC metabus-organic-changelog-2026-09-03 R3 amend, MED 6): this
 * whole page still reflects the pre-R3 status model (`draft`/`scheduled`/
 * `publishing`/`published`/`failed` - missing `held`/`needs_revision`/
 * `approved`) and its compose dialog still sends a client `status`, a field
 * R3's `SocialPostCreate` no longer has. The "Publish now" button was
 * REMOVED here (it always 409s under R3's state machine: a freshly created
 * post is `draft`/`held`, never `scheduled`/`failed`, and `usePublishSocialPost`
 * has no companion approve/schedule caller wired into this UI) rather than
 * left dangling. The full rework - the new status model, an approve/schedule
 * flow, the consent gate - is R1's job (the portal `social` view); this page
 * is dead-until-then. `publishSocialPost`/`usePublishSocialPost` remain
 * exported (still used by the reference client / smoke path) even though
 * this page no longer calls them.
 *
 * Gap #8: this is its OWN page - NOT an inbox tab. A social post is not a
 * conversation; this page does not touch the conversation thread view.
 */

import { useMemo, useState } from 'react';
import { CalendarDays, Plus, Trash2, Pencil, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCanWrite } from '@/lib/auth/AuthProvider';
import {
  useSocialPosts,
  useCreateSocialPost,
  useUpdateSocialPost,
  useDeleteSocialPost,
} from '@/lib/api/hooks/useSocialPosts';
import {
  SOCIAL_NETWORKS,
  type PerNetworkStatus,
  type SocialPost,
} from '@/types/socialPost';

// metabus tint status pills (SPEC FE-1 / R2): per-network + post status use the
// tint-bg + tint-border tones rather than solid fills.
function statusVariant(status: string): 'success' | 'warning' | 'error' | 'neutral' {
  switch (status) {
    case 'published':
      return 'success';
    case 'failed':
      return 'error';
    case 'publishing':
    case 'scheduled':
      return 'warning';
    default:
      return 'neutral';
  }
}

function perNetworkVariant(status: PerNetworkStatus): 'success' | 'warning' | 'error' | 'neutral' {
  if (status === 'published') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'no_token' || status === 'skipped') return 'neutral';
  return 'warning';
}

function formatWhen(iso?: string | null): string {
  if (!iso) return 'No date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'No date';
  return d.toLocaleString();
}

function networkLabel(value: string): string {
  return SOCIAL_NETWORKS.find((n) => n.value === value)?.label ?? value;
}

interface ComposeState {
  open: boolean;
  editingId: string | null;
  title: string;
  caption: string;
  mediaUrls: string;
  networks: string[];
  scheduledAt: string;
  schedule: boolean; // true = scheduled, false = draft
}

const EMPTY_COMPOSE: ComposeState = {
  open: false,
  editingId: null,
  title: '',
  caption: '',
  mediaUrls: '',
  networks: [],
  scheduledAt: '',
  schedule: true,
};

export function SocialPlannerPage() {
  const canWrite = useCanWrite();
  const { data, isLoading, error, refetch } = useSocialPosts();
  const createMutation = useCreateSocialPost();
  const updateMutation = useUpdateSocialPost();
  const deleteMutation = useDeleteSocialPost();

  const [compose, setCompose] = useState<ComposeState>(EMPTY_COMPOSE);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const posts = useMemo(() => {
    const list = [...(data?.posts ?? [])];
    list.sort((a, b) => {
      const at = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Infinity;
      const bt = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Infinity;
      return at - bt;
    });
    return list;
  }, [data]);

  const openCompose = () => setCompose({ ...EMPTY_COMPOSE, open: true });

  const openEdit = (post: SocialPost) => {
    setCompose({
      open: true,
      editingId: post.id,
      title: post.title,
      caption: post.caption,
      mediaUrls: (post.media_urls ?? []).join('\n'),
      networks: post.networks ?? [],
      scheduledAt: post.scheduled_at ? post.scheduled_at.slice(0, 16) : '',
      schedule: post.status !== 'draft',
    });
  };

  const closeCompose = () => setCompose(EMPTY_COMPOSE);

  const toggleNetwork = (value: string) => {
    setCompose((c) => ({
      ...c,
      networks: c.networks.includes(value)
        ? c.networks.filter((n) => n !== value)
        : [...c.networks, value],
    }));
  };

  const handleSave = async () => {
    if (!compose.title.trim()) {
      toast.error('A title is required');
      return;
    }
    if (compose.networks.length === 0) {
      toast.error('Select at least one network');
      return;
    }
    if (!compose.scheduledAt) {
      toast.error('Pick a date/time');
      return;
    }
    const mediaUrls = compose.mediaUrls
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);
    const scheduledIso = new Date(compose.scheduledAt).toISOString();
    const status = compose.schedule ? 'scheduled' : 'draft';

    try {
      if (compose.editingId) {
        await updateMutation.mutateAsync({
          id: compose.editingId,
          data: {
            title: compose.title,
            caption: compose.caption,
            media_urls: mediaUrls,
            networks: compose.networks,
            scheduled_at: scheduledIso,
            status,
          },
        });
        toast.success('Post updated');
      } else {
        await createMutation.mutateAsync({
          title: compose.title,
          caption: compose.caption,
          media_urls: mediaUrls,
          networks: compose.networks,
          scheduled_at: scheduledIso,
          status,
        });
        toast.success('Post created');
      }
      closeCompose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save post');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteMutation.mutateAsync(deleteConfirm);
      toast.success('Post deleted');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete post');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
            <CalendarDays className="h-6 w-6" />
            Social Planner
          </h1>
          <p className="text-sm text-muted-foreground">
            Schedule multi-network posts (Instagram, Facebook, LinkedIn)
          </p>
        </div>
        {canWrite && (
          <Button onClick={openCompose}>
            <Plus className="h-4 w-4 mr-2" />
            New Post
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {error && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-muted-foreground">
                Failed to load posts: {error.message}
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && posts.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <CalendarDays className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No posts scheduled yet.</p>
              {canWrite && (
                <Button onClick={openCompose}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create your first post
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && posts.length > 0 && (
          <div className="space-y-3" data-testid="social-post-list">
            {posts.map((post) => (
              <Card key={post.id}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{post.title}</span>
                        <Badge variant={statusVariant(post.status)}>{post.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatWhen(post.scheduled_at)}
                      </p>
                      {post.caption && (
                        <p className="text-sm mt-2 line-clamp-2">{post.caption}</p>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap mt-2">
                        {(post.networks ?? []).map((net) => {
                          const pn = post.per_network?.[net];
                          return (
                            <Badge
                              key={net}
                              variant={pn ? perNetworkVariant(pn.status) : 'outline'}
                            >
                              {pn ? `${networkLabel(net)}: ${pn.status}` : networkLabel(net)}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                    {canWrite && (
                      <div className="flex items-center gap-1 shrink-0">
                        {post.status !== 'published' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(post)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirm(post.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Compose / edit dialog */}
      <Dialog open={compose.open} onOpenChange={(o) => !o && closeCompose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{compose.editingId ? 'Edit Post' : 'New Post'}</DialogTitle>
            <DialogDescription>
              Compose a multi-network post and schedule when it publishes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sp-title">Title (internal)</Label>
              <Input
                id="sp-title"
                value={compose.title}
                onChange={(e) => setCompose((c) => ({ ...c, title: e.target.value }))}
                placeholder="e.g. Spring promo carousel"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-caption">Caption</Label>
              <Textarea
                id="sp-caption"
                value={compose.caption}
                onChange={(e) => setCompose((c) => ({ ...c, caption: e.target.value }))}
                placeholder="Post caption…"
                rows={4}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-media">Media URLs (one per line)</Label>
              <Textarea
                id="sp-media"
                value={compose.mediaUrls}
                onChange={(e) => setCompose((c) => ({ ...c, mediaUrls: e.target.value }))}
                placeholder={'https://…/1.jpg\nhttps://…/2.jpg'}
                rows={3}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Networks</Label>
              <div className="flex flex-wrap gap-2">
                {SOCIAL_NETWORKS.map((n) => (
                  <Button
                    key={n.value}
                    type="button"
                    variant={compose.networks.includes(n.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleNetwork(n.value)}
                  >
                    {n.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-when">Scheduled at</Label>
              <Input
                id="sp-when"
                type="datetime-local"
                value={compose.scheduledAt}
                onChange={(e) => setCompose((c) => ({ ...c, scheduledAt: e.target.value }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={compose.schedule ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCompose((c) => ({ ...c, schedule: true }))}
              >
                Schedule
              </Button>
              <Button
                type="button"
                variant={!compose.schedule ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCompose((c) => ({ ...c, schedule: false }))}
              >
                Save as draft
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeCompose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the scheduled post. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SocialPlannerPage;
